import { getOrderStatusDisplay } from "@/lib/statusConfig";

interface StatusBadgeProps {
  status: string;
  type?: "order" | "driver" | "priority" | "problem";
}

type StatusColors = {
  bg: string;
  dot: string;
};

type StatusConfig = {
  [key: string]: {
    colors: StatusColors;
    text?: string;
  };
};

const STATUS_CONFIGS: Record<string, StatusConfig> = {
  driver: {
    "In Transit": {
      colors: { bg: "bg-yellow-50 text-yellow-700", dot: "text-yellow-400" },
    },
    Delivered: {
      colors: { bg: "bg-green-50 text-green-700", dot: "text-green-400" },
    },
    Assigned: {
      colors: { bg: "bg-blue-50 text-blue-700", dot: "text-blue-400" },
    },
    Failed: { colors: { bg: "bg-red-50 text-red-700", dot: "text-red-400" } },
  },
  priority: {
    High: {
      colors: { bg: "bg-red-50 text-red-700", dot: "text-red-400" },
      text: "Alta",
    },
    Medium: {
      colors: { bg: "bg-yellow-50 text-yellow-700", dot: "text-yellow-400" },
      text: "Media",
    },
    Low: {
      colors: { bg: "bg-green-50 text-green-700", dot: "text-green-400" },
      text: "Baja",
    },
  },
  problem: {
    Failed: { colors: { bg: "bg-red-50 text-red-700", dot: "text-red-400" } },
    Cancelled: {
      colors: { bg: "bg-gray-50 text-gray-700", dot: "text-gray-400" },
    },
    Delayed: {
      colors: { bg: "bg-yellow-50 text-yellow-700", dot: "text-yellow-400" },
    },
  },
};

const DEFAULT_COLORS: StatusColors = {
  bg: "bg-gray-50 text-gray-700",
  dot: "text-gray-400",
};

export default function StatusBadge({
  status,
  type = "order",
}: StatusBadgeProps) {
  if (!status) return null;

  // Para órdenes, usar la nueva configuración centralizada
  if (type === "order") {
    const statusConfig = getOrderStatusDisplay(status);
    return (
      <span
        className={`
          inline-flex items-center
          px-2 py-0.5
          text-xs font-medium
          rounded-full
          ${statusConfig.color}
        `}
      >
        {statusConfig.display}
      </span>
    );
  }

  // Para otros tipos (driver, priority, problem), usar la configuración existente
  const normalizedStatus = status.toLowerCase();
  const config =
    STATUS_CONFIGS[type]?.[normalizedStatus] || STATUS_CONFIGS[type]?.[status];
  const colors = config?.colors || DEFAULT_COLORS;
  const displayText = config?.text || status;

  return (
    <span
      className={`
        inline-flex items-center
        px-2 py-0.5
        text-xs font-medium
        rounded-full
        ${colors.bg}
      `}
    >
      <span className={`mr-1 ${colors.dot}`}>●</span>
      {displayText}
    </span>
  );
}
